/**
 * Follow-up lineage predicates — the ONE definition of "is this portfolio a
 * follow-up?" and "which comparison label variant does it get?".
 *
 * A follow-up portfolio carries exactly one lineage reference: account
 * follow-ups `sourceAccountId`, agreement (savings-plan) follow-ups
 * `sourceSavingsPlanId`. Every consumer — the advisor and public portfolio
 * page renderers, the PDF field-value emission gate, the PDF component
 * converter — must derive follow-up-ness through these helpers rather than
 * re-spelling the field check: the rule has already changed shape once
 * (account-only → account-or-agreement) and each hand-written copy missed
 * during that change was a shipped bug.
 *
 * Structural typing on purpose: Drizzle rows, renderer DTOs and the PDF
 * converter's narrow section ctx all just carry the two columns.
 */

type FollowupLineage = {
  sourceAccountId?: string | null;
  sourceSavingsPlanId?: string | null;
};

/** A portfolio is a follow-up when it carries either lineage reference. */
export const isFollowupPortfolio = (p: FollowupLineage | null | undefined): boolean =>
  !!(p?.sourceAccountId || p?.sourceSavingsPlanId);

/**
 * Agreement follow-ups label the existing-vs-modified comparison
 * "Savings Plan"; account follow-ups (and non-follow-ups) "Portfolio".
 */
export const followupComparisonVariant = (
  p: FollowupLineage | null | undefined,
): "portfolio" | "savingsPlan" => (p?.sourceSavingsPlanId ? "savingsPlan" : "portfolio");

/**
 * QL-309 — a follow-up portfolio's schedule type is derived from what it
 * follows up and cannot be changed: an agreement follow-up IS the existing
 * bank agreement (always recurring); an account follow-up rebalances the
 * account's holdings (always one-time — recurring orders are never generated
 * for it). Null = not a follow-up, the schedule is the caller's choice.
 */
export const lockedScheduleType = (
  p: FollowupLineage | null | undefined,
): "one_time" | "recurring" | null =>
  p?.sourceSavingsPlanId ? "recurring" : p?.sourceAccountId ? "one_time" : null;

/**
 * QL-398 — an ACCOUNT follow-up: its custom rows are TARGET holdings against
 * the frozen existing snapshot, and the orders it emits are the deltas (plus
 * one switch order per switch pair). Every rule that judges "what is bought"
 * — the min-buy guards and gate — must branch on this, never on the row
 * amount alone. Reads lineage, not `scheduleType`: order emission already
 * emits one-time deltas for any portfolio with `sourceAccountId`
 * (`producesRecurringOrders`), legacy pre-QL-309 recurring rows included.
 */
export const isAccountFollowupPortfolio = (p: FollowupLineage | null | undefined): boolean =>
  lockedScheduleType(p) === "one_time";
